import React, { type ChangeEvent, useState, useCallback, useMemo } from 'react';
import { type UIAnalyticsEvent, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { CardListId } from '../../../model/types';
import { useBacklogSelector, useBacklogDispatch } from '../../../state';
import { toggleIncludeSubtasks as toggleIncludeSubtasksAction } from '../../../state/actions/workload-modal';
import { getTimeTrackingOptions } from '../../../state/selectors/app';
import {
	getEstimationName,
	getEstimationType,
	getIsRemainingTimeTrackingEnabled,
} from '../../../state/selectors/estimation';
import { makeWorkLoadByAssignee, getIncludeSubtasks } from '../../../state/selectors/workload';
import WorkLoadByAssignee from './view';

type Props = { cardListId: CardListId; sprintName: string };

const WorkLoadByAssigneeContainer = ({ cardListId, sprintName }: Props) => {
	const [isModalOpen, setModal] = useState(false);
	const dispatch = useBacklogDispatch();

	const onToggleModal = useCallback(() => {
		setModal((open) => !open);
	}, [setModal]);

	const assignees = useBacklogSelector(makeWorkLoadByAssignee)(cardListId);
	const estimationType = useBacklogSelector(getEstimationType);
	const estimationFieldName = useBacklogSelector(getEstimationName);
	const isRemainingTimeTrackingEnabled = useBacklogSelector(getIsRemainingTimeTrackingEnabled);
	const isEstimationEnabled = !!estimationType && estimationType !== 'ISSUE_COUNT';
	const timeTrackingOptions = useBacklogSelector(getTimeTrackingOptions);
	const includeSubtasks = useBacklogSelector(getIncludeSubtasks);
	const toggleIncludeSubtasks = (
		event: ChangeEvent<HTMLInputElement>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		dispatch(toggleIncludeSubtasksAction());
		fireUIAnalytics(analyticsEvent, 'includeSubtasks', { includeSubtasks: !includeSubtasks });
	};

	const filterUnassignedAssignees = assignees.filter((assignee) => assignee.name !== undefined);
	const unassigned = useMemo(
		() => assignees.find((assignee) => assignee.name === undefined),
		[assignees],
	);

	return filterUnassignedAssignees.length ? (
		<WorkLoadByAssignee
			assignees={filterUnassignedAssignees}
			estimationType={estimationType}
			isEstimationEnabled={isEstimationEnabled}
			isRemainingTimeTrackingEnabled={isRemainingTimeTrackingEnabled}
			onToggleModal={onToggleModal}
			isModalOpen={isModalOpen}
			unassigned={unassigned}
			sprintName={sprintName}
			timeTrackingOptions={timeTrackingOptions}
			estimationFieldName={estimationFieldName}
			includeSubtasks={includeSubtasks}
			onToggleIncludeSubtasks={toggleIncludeSubtasks}
		/>
	) : null;
};

export default WorkLoadByAssigneeContainer;
